import { useEffect, useRef, useState } from 'react'
import {
  runShareAction,
  SHARE_ACTION_LABELS,
  SHARE_ACTIONS,
  type ShareAction,
} from '../../utils/shareActions'
import { cx } from './utils'

export type ShareMenuAction = ShareAction | 'pdf'

type ShareActionsMenuProps = {
  message: string | null | undefined
  copyMessage?: string | null | undefined
  disabled?: boolean
  copyLabel?: string
  emailSubject?: string
  emailTo?: string
  label?: string
  actions?: ShareMenuAction[]
  actionLabels?: Partial<Record<ShareMenuAction, string>>
  layout?: 'inline' | 'menu'
  onPdfDownload?: () => void | Promise<void>
  pdfBusy?: boolean
  onAfterAction?: (action: ShareMenuAction) => void
}

function IconCopy({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M8.25 7.5h9a2.25 2.25 0 0 1 2.25 2.25v9a2.25 2.25 0 0 1-2.25 2.25h-9a2.25 2.25 0 0 1-2.25-2.25v-9A2.25 2.25 0 0 1 8.25 7.5Z"
        stroke="currentColor"
        strokeWidth={1.75}
      />
      <path
        d="M6.75 5.25h9a2.25 2.25 0 0 1 2.25 2.25"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth={1.75}
      />
    </svg>
  )
}

function IconMail({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3.75 6.75h16.5M4.5 6.75l7.5 6 7.5-6M5.25 17.25h13.5a1.5 1.5 0 0 0 1.5-1.5v-9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v9a1.5 1.5 0 0 0 1.5 1.5Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.75}
      />
    </svg>
  )
}

function IconPdf({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M7.5 3.75h6.75L18.75 8.25v11.25a1.5 1.5 0 0 1-1.5 1.5h-10.5a1.5 1.5 0 0 1-1.5-1.5V5.25a1.5 1.5 0 0 1 1.5-1.5Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.75}
      />
      <path
        d="M14.25 3.75v4.5h4.5M8.25 12.75h7.5M8.25 15.75h4.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth={1.75}
      />
    </svg>
  )
}

function IconWhatsApp({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"
        fill="currentColor"
      />
    </svg>
  )
}

function IconViber({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2C6.477 2 2 6.145 2 11.243c0 1.49.39 2.89 1.07 4.12L2 22l6.95-1.95A9.77 9.77 0 0 0 12 20.486C17.523 20.486 22 16.34 22 11.243 22 6.145 17.523 2 12 2Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth={1.75}
      />
      <path
        d="M8.5 10.5h7M8.5 13.5h4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth={1.75}
      />
    </svg>
  )
}

function IconChevronDown({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d="m5 7.5 5 5 5-5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
    </svg>
  )
}

const ACTION_ICONS: Partial<Record<ShareMenuAction, typeof IconCopy>> = {
  copy: IconCopy,
  email: IconMail,
  viber: IconViber,
  whatsapp: IconWhatsApp,
  pdf: IconPdf,
}

const ACTION_HINTS: Partial<Record<ShareMenuAction, string>> = {
  copy: 'Στο πρόχειρο',
  viber: 'Αποστολή μέσω Viber',
  whatsapp: 'Αποστολή μέσω WhatsApp',
  email: 'Άνοιγμα email εφαρμογής',
  pdf: 'Λήψη αρχείου PDF',
}

const iconToneClass: Record<string, string> = {
  copy: 'bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300',
  viber: 'bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300',
  whatsapp: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
  email: 'bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300',
  pdf: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
}

function actionLabel(
  action: ShareMenuAction,
  labels?: Partial<Record<ShareMenuAction, string>>,
  pdfBusy?: boolean,
): string {
  if (action === 'pdf' && pdfBusy) return 'Δημιουργία PDF…'
  if (labels?.[action]) return labels[action]!
  if (action === 'pdf') return 'Κατέβασμα PDF'
  return SHARE_ACTION_LABELS[action as ShareAction]
}

export function ShareActionsMenu({
  message,
  copyMessage,
  disabled = false,
  copyLabel = 'Ο σύνδεσμος αντιγράφηκε.',
  emailSubject,
  emailTo,
  label = 'Ενέργειες',
  actions = SHARE_ACTIONS,
  actionLabels,
  layout,
  onPdfDownload,
  pdfBusy = false,
  onAfterAction,
}: ShareActionsMenuProps) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDetailsElement>(null)
  const canShare = Boolean(message?.trim()) && !disabled
  const menuEnabled = canShare || Boolean(onPdfDownload)
  const useInline =
    layout === 'inline' ||
    (layout !== 'menu' && actions.length <= 2 && !actions.includes('pdf'))

  useEffect(() => {
    if (!open || useInline) return
    const onPointerDown = (e: PointerEvent) => {
      const el = menuRef.current
      if (el && !el.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, useInline])

  function isActionDisabled(action: ShareMenuAction): boolean {
    if (!canShare && action !== 'pdf') return true
    if (action === 'pdf') return pdfBusy || !onPdfDownload
    return false
  }

  async function onSelect(action: ShareMenuAction) {
    setOpen(false)
    if (action === 'pdf') {
      if (onPdfDownload) await onPdfDownload()
      onAfterAction?.(action)
      return
    }
    const payload =
      action === 'copy' ? (copyMessage ?? message ?? '') : (message ?? '')
    await runShareAction(action, payload, { copyLabel, emailSubject, emailTo })
    onAfterAction?.(action)
  }

  function renderMenuItem(action: ShareMenuAction, inPanel = false) {
    const Icon = ACTION_ICONS[action] ?? IconCopy
    const itemDisabled = isActionDisabled(action)
    const hint = ACTION_HINTS[action]

    return (
      <button
        key={action}
        type="button"
        disabled={itemDisabled}
        onClick={() => void onSelect(action)}
        className={cx(
          'flex w-full items-center gap-3 text-left transition disabled:cursor-not-allowed disabled:opacity-50',
          inPanel
            ? 'rounded-lg px-2 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-800/80'
            : 'rounded-xl border border-zinc-200/90 bg-white px-3 py-2.5 shadow-sm hover:border-violet-200 hover:bg-violet-50/50 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-violet-800 dark:hover:bg-violet-950/30',
        )}
      >
        <span
          className={cx(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
            iconToneClass[action] ?? iconToneClass.copy,
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            {actionLabel(action, actionLabels, pdfBusy)}
          </span>
          {hint && inPanel ? (
            <span className="mt-0.5 block text-xs text-zinc-500 dark:text-zinc-400">
              {hint}
            </span>
          ) : null}
        </span>
      </button>
    )
  }

  if (useInline) {
    return (
      <div
        className="inline-flex flex-wrap gap-2 rounded-xl border border-zinc-200/90 bg-zinc-50/80 p-1 dark:border-zinc-700 dark:bg-zinc-950/40"
        role="group"
        aria-label="Ενέργειες"
      >
        {actions.map((action) => {
          const Icon = ACTION_ICONS[action] ?? IconCopy
          const itemDisabled = isActionDisabled(action)
          return (
            <button
              key={action}
              type="button"
              disabled={itemDisabled}
              onClick={() => void onSelect(action)}
              className={cx(
                'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40 disabled:cursor-not-allowed disabled:opacity-50',
                action === 'email'
                  ? 'bg-white text-zinc-800 shadow-sm hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-100'
                  : 'bg-violet-600 text-white shadow-sm hover:bg-violet-500',
              )}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              {actionLabel(action, actionLabels, pdfBusy)}
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <details
      ref={menuRef}
      className="group relative isolate z-40 [&>summary::-webkit-details-marker]:hidden"
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary
        className={cx(
          'flex cursor-pointer list-none items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/50',
          menuEnabled
            ? 'bg-violet-600 text-white hover:bg-violet-500 dark:bg-violet-600 dark:hover:bg-violet-500'
            : 'cursor-not-allowed bg-zinc-200 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-500',
        )}
        aria-disabled={!menuEnabled}
        onClick={(e) => {
          if (!menuEnabled) e.preventDefault()
        }}
      >
        {label}
        <IconChevronDown
          className={cx(
            'h-4 w-4 opacity-90 transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </summary>
      {menuEnabled ? (
        <div className="absolute left-0 top-[calc(100%+0.375rem)] z-50 w-[min(100%,16.5rem)] overflow-hidden rounded-xl border border-zinc-200/90 bg-white p-1.5 shadow-xl ring-1 ring-black/5 dark:border-zinc-700 dark:bg-zinc-900 dark:ring-white/10">
          <p className="px-2 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
            Επιλογή ενέργειας
          </p>
          <div className="flex flex-col gap-0.5">
            {actions.map((action) => renderMenuItem(action, true))}
          </div>
        </div>
      ) : null}
    </details>
  )
}
